/**
 * The trash.
 *
 * Every screen in the product could send photographs here and no screen could
 * show them. The wording said "moved to trash", the settings page explained
 * that deleted photographs wait thirty days, and there was nowhere to go and
 * look — so "move to trash" read as a polite word for delete, and anything sent
 * there by mistake was gone as far as anybody could tell.
 *
 * Two lists, because there are two permissions. Anyone can put their own
 * photographs back; restoring somebody else's needs `file:restore`, and a
 * member without it should not be shown a list of things they cannot act on.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatBytes } from "@fc/core";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { useToast } from "../../components/ui/Toast.js";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import "./TrashView.css";

export interface TrashViewProps {
  groupId: string;
  /** Whether this member may restore photographs that are not theirs. */
  canRestoreAny: boolean;
}

interface TrashRow {
  id: string;
  name: string;
  deletedAt: Date | string | null;
  purgesAt: Date | string | null;
  sizeBytes?: number;
}

/** "in 12 days", or "any time now" once the date has passed. */
function daysLeft(
  purgesAt: Date | string | null,
  t: (text: string) => string,
): string {
  if (!purgesAt) return t("Waiting to be cleared");
  const when = new Date(purgesAt).getTime();
  const days = Math.ceil((when - Date.now()) / 86_400_000);
  if (days <= 0) return t("Clearing any time now");
  if (days === 1) return t("Cleared tomorrow");
  return `${t("Cleared in")} ${String(days)} ${t("days")}`;
}

export function TrashView({ groupId, canRestoreAny }: TrashViewProps) {
  const t = useT();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  /*
   * The wide list when the permission allows it, and your own otherwise. Only
   * one is ever fetched: asking for the group's trash without `file:restore`
   * is a 403, and a screen that fires a request it knows will be refused makes
   * the network log a poor place to notice real problems.
   */
  const groupTrash = useQuery({
    queryKey: ["trash", groupId],
    queryFn: () => trpcClient.media.trash.query({ groupId }),
    enabled: canRestoreAny,
  });

  const ownTrash = useQuery({
    queryKey: ["trash-own", groupId],
    queryFn: () => trpcClient.media.ownTrash.query({ groupId }),
    enabled: !canRestoreAny,
  });

  const rows: TrashRow[] = useMemo(
    () => (canRestoreAny ? (groupTrash.data ?? []) : (ownTrash.data ?? [])),
    [canRestoreAny, groupTrash.data, ownTrash.data],
  );

  const isLoading = canRestoreAny ? groupTrash.isLoading : ownTrash.isLoading;

  const restore = useMutation({
    mutationFn: (fileIds: string[]) =>
      canRestoreAny
        ? trpcClient.media.restore.mutate({ groupId, fileIds })
        : trpcClient.media.restoreOwn.mutate({ groupId, fileIds }),
    onSuccess: (result) => {
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ["trash", groupId] });
      void queryClient.invalidateQueries({ queryKey: ["trash-own", groupId] });
      void queryClient.invalidateQueries({ queryKey: ["gallery", groupId] });
      toast(`${t("Put back")} ${String(result.restored)}.`);
    },
    onError: (error) => toast(errorMessage(error), { variant: "danger" }),
  });

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="fc-trash">
      <header className="fc-trash__header">
        <h1>{t("Trash")}</h1>
        <p className="fc-trash__note">
          {t(
            "Photos wait here before they are gone for good. Put anything back while it is still listed.",
          )}
        </p>
      </header>

      {selected.size > 0 && (
        <div
          className="fc-trash__bar"
          role="toolbar"
          aria-label={t("Selected")}
        >
          <span className="fc-tabular">{selected.size}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelected(new Set())}
          >
            {t("Clear selection")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={restore.isPending}
            onClick={() => restore.mutate([...selected])}
          >
            {t("Put back")}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="fc-trash__list">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} height="56px" radius="md" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4.5 7h15M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7" />
              <path d="M6.5 7l.8 12a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
            </svg>
          }
          title={t("The trash is empty")}
          description={t(
            "Nothing has been deleted here, or everything has already been cleared.",
          )}
        />
      ) : (
        <ul className="fc-trash__list" role="list">
          {rows.map((row) => {
            const isSelected = selected.has(row.id);
            return (
              <li key={row.id}>
                <button
                  type="button"
                  className="fc-trash__row"
                  data-selected={isSelected || undefined}
                  aria-pressed={isSelected}
                  onClick={() => toggle(row.id)}
                >
                  <span className="fc-trash__check" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M5 12.5l4.5 4.5L19 7.5" />
                    </svg>
                  </span>
                  <span className="fc-trash__name">{row.name}</span>
                  <span className="fc-trash__meta">
                    {row.sizeBytes === undefined ? null : (
                      <span className="fc-tabular">
                        {formatBytes(row.sizeBytes)}
                      </span>
                    )}
                    <span>{daysLeft(row.purgesAt, t)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
